type UseScrollSnapDotsOptions<T> = {
  getItemKey?: (item: T, index: number) => string | number
}

export const useScrollSnapDots = <T>(
  items: MaybeRefOrGetter<T[]>,
  options: UseScrollSnapDotsOptions<T> = {}
) => {
  const scrollEl = ref<HTMLElement | null>(null)
  const activeIndex = ref(0)

  const itemCount = computed(() => toValue(items).length)

  const getItemKey = (item: T, index: number) =>
    options.getItemKey?.(item, index) ?? index

  const getActiveIndex = (el: HTMLElement) => {
    const count = itemCount.value

    if (!count || !el.children.length) {
      return 0
    }

    const maxScrollLeft = el.scrollWidth - el.clientWidth

    if (maxScrollLeft <= 0) {
      return 0
    }

    if (el.scrollLeft >= maxScrollLeft - 1) {
      return count - 1
    }

    if (el.scrollLeft <= 1) {
      return 0
    }

    const centerX = el.getBoundingClientRect().left + el.clientWidth / 2

    let closestIndex = 0
    let closestDistance = Infinity

    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i] as HTMLElement
      const childRect = child.getBoundingClientRect()
      const childCenterX = childRect.left + childRect.width / 2
      const distance = Math.abs(childCenterX - centerX)

      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = i
      }
    }

    return closestIndex
  }

  const handleScroll = () => {
    const el = scrollEl.value

    if (!el) {
      return
    }

    activeIndex.value = getActiveIndex(el)
  }

  const handleDotClick = (index: number) => {
    const el = scrollEl.value
    const item = el?.children[index] as HTMLElement | undefined

    if (!el || !item) {
      return
    }

    activeIndex.value = index

    const count = itemCount.value
    const inline =
      index === 0 ? 'start' : index === count - 1 ? 'end' : 'center'

    item.scrollIntoView({
      behavior: 'smooth',
      inline,
      block: 'nearest'
    })
  }

  const hasSameItemsPrefix = (newItems: T[], oldItems: T[]) =>
    oldItems.every(
      (item, index) =>
        getItemKey(item, index) === getItemKey(newItems[index], index)
    )

  watch(
    () => toValue(items),
    (newItems, oldItems) => {
      if (!oldItems?.length) {
        return
      }

      if (
        newItems.length >= oldItems.length &&
        hasSameItemsPrefix(newItems, oldItems)
      ) {
        nextTick(handleScroll)
        return
      }

      activeIndex.value = 0
      scrollEl.value?.scrollTo({ left: 0 })
    }
  )

  return {
    scrollEl,
    activeIndex,
    handleScroll,
    handleDotClick
  }
}
